package com.dotdrives.web.pages.forms

import com.dotdrives.model.GivingFormStatus
import react.*
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.textarea
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.p
import react.dom.aria.AriaRole
import web.cssom.ClassName
import web.html.ButtonType
import web.html.InputType
import web.html.button as buttonType
import web.html.text
import web.html.datetimeLocal

internal external interface ToggleRowProps : Props {
    var name: String
    var sub: String
    var on: Boolean
    var enabled: Boolean
    var onToggle: (Boolean) -> Unit
}
internal val ToggleRow = FC<ToggleRowProps> { props ->
    div {
        className = ClassName("forms-toggle-row")
        div {
            div { className = ClassName("forms-toggle-name"); +props.name }
            div { className = ClassName("forms-toggle-sub"); +props.sub }
        }
        SwitchButton { on = props.on; enabled = props.enabled; label = props.name; onToggle = props.onToggle }
    }
}
internal external interface SwitchButtonProps : Props {
    var on: Boolean
    var enabled: Boolean
    var label: String
    var onToggle: (Boolean) -> Unit
}
internal val SwitchButton = FC<SwitchButtonProps> { props ->
    button {
        type = ButtonType.buttonType
        className = ClassName("forms-switch" + if (props.on) " forms-switch-on" else "")
        role = AriaRole.switch
        ariaChecked = props.on.unsafeCast<react.dom.aria.AriaChecked>()
        disabled = !props.enabled
        ariaLabel = props.label
        onClick = { if (props.enabled) props.onToggle(!props.on) }
        span { className = ClassName("forms-switch-knob") }
    }
}

val FormDestinationEditor = FC<FormTabProps> { props ->
    val state = props.state
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Donation target" }
        select {
            className = ClassName("forms-select"); ariaLabel = "Donation target"
            value = state.draftTargetId ?: ""; disabled = !props.canWrite
            onChange = { if (props.canWrite) props.controller.setTargetId(it.currentTarget.value.takeIf(String::isNotBlank)) }
            option { value = ""; +"No target" }
            val selected = state.draftTargetId
            if (selected != null && state.targets.none { it.id == selected }) {
                option { value = selected; +(state.form?.targetName ?: "Existing target") }
            }
            state.targets.forEach { target -> option { key = target.id.unsafeCast<Key>(); value = target.id; +target.name } }
        }
    }
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Campaign goal ($)" }
        input {
            type = InputType.text; className = ClassName("forms-text-input"); value = state.draftGoalAmount
            ariaLabel = "Campaign goal"; placeholder = "e.g. 400,000"; disabled = !props.canWrite
            onChange = { if (props.canWrite) props.controller.setGoalAmount(it.currentTarget.value) }
        }
    }
    ToggleRow {
        name = "Show progress bar"; sub = "Publish this target’s net gifts toward the goal"
        on = state.draftShowProgressBar; enabled = props.canWrite
        onToggle = { if (props.canWrite) props.controller.setShowProgressBar(it) }
    }
    p { className = ClassName("forms-field-note"); +"Public progress requires a target and a positive goal. It uses the target’s configured dates, or all time when undated. Preview does not invent a progress total." }
}

external interface FormSettingsTabProps : FormTabProps { var orgId: String }
val FormSettingsTab = FC<FormSettingsTabProps> { props ->
    val state = props.state
    val form = state.form
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Form name (internal)" }
        input {
            type = InputType.text; className = ClassName("forms-text-input"); value = state.draftName
            disabled = !props.canWrite; ariaLabel = "Form name"
            onChange = { if (props.canWrite) props.controller.setName(it.currentTarget.value) }
        }
    }
    div {
        className = ClassName("forms-field-block")
        div { className = ClassName("forms-field-label"); +"Public URL" }
        if (form?.status == GivingFormStatus.LIVE) {
            div {
                className = ClassName("forms-readonly-url")
                span { +publicFormUrl(props.orgId, form.publicSlug) }
                button { type = ButtonType.buttonType; ariaLabel = "Copy public link"; onClick = { copyFormLink(props.orgId, form.publicSlug) }; +"Copy" }
            }
            p { className = ClassName("forms-field-note"); +"Online giving must be enabled and the form open to accept gifts." }
        } else p {
            className = ClassName("forms-field-note")
            +if (form?.status == GivingFormStatus.ARCHIVED) "This archived form has no active public link. Use Full preview to test it here."
            else "This draft is not published. Use Full preview to test it without creating a public link."
        }
    }
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Closing date and time (UTC)" }
        input {
            type = InputType.datetimeLocal; className = ClassName("forms-text-input"); value = state.draftCloseAt
            ariaLabel = "Closing date and time (UTC)"; disabled = !props.canWrite
            onChange = { if (props.canWrite) props.controller.setCloseAt(it.currentTarget.value) }
        }
    }
    p { className = ClassName("forms-field-note"); +"Leave blank to keep the form open. After this time, the public form stops accepting gifts. Save changes to apply." }
    if (state.draftCloseAt.isNotBlank()) button {
        type = ButtonType.buttonType; className = ClassName("forms-v2-text-button"); disabled = !props.canWrite
        onClick = { if (props.canWrite) props.controller.setCloseAt("") }; +"Remove closing date"
    }
    if (form?.status != GivingFormStatus.ARCHIVED) div {
        className = ClassName("forms-settings-row")
        div { div { className = ClassName("forms-settings-row-title"); +"Archive form" }; div { className = ClassName("forms-settings-row-sub"); +"Stops public giving and keeps existing data." } }
        button {
            type = ButtonType.buttonType; className = ClassName("forms-archive-btn"); disabled = !props.canWrite
            onClick = { if (props.canWrite) props.controller.archive() }; +"Archive form"
        }
    }
}

external interface FormReceiptsTabProps : FormTabProps
val FormReceiptsTab = FC<FormReceiptsTabProps> { props ->
    val state = props.state
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Receipt subject line" }
        input {
            type = InputType.text; className = ClassName("forms-text-input"); value = state.draftReceiptSubject
            disabled = !props.canWrite; ariaLabel = "Receipt subject line"
            onChange = { if (props.canWrite) props.controller.setReceiptSubject(it.currentTarget.value) }
        }
    }
    label {
        className = ClassName("forms-field-block")
        span { className = ClassName("forms-field-label"); +"Receipt message" }
        textarea {
            className = ClassName("forms-textarea"); rows = 9; value = state.draftReceiptMessage
            disabled = !props.canWrite; ariaLabel = "Receipt message"
            onChange = { if (props.canWrite) props.controller.setReceiptMessage(it.currentTarget.value) }
        }
    }
    div { className = ClassName("forms-token-row"); listOf("{first_name}", "{amount}", "{date}", "{ein}").forEach { token -> span { key = token.unsafeCast<Key>(); className = ClassName("forms-token-pill"); +token } } }
    p { className = ClassName("forms-field-note"); +"Use these tokens in your message. Save changes above to apply receipt settings." }
    ToggleRow {
        name = "Include IRS tax language"; sub = "No goods or services were provided…"
        on = state.draftIncludeIrsLanguage; enabled = props.canWrite
        onToggle = { if (props.canWrite) props.controller.setIncludeIrsLanguage(it) }
    }
}
